import { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Input } from "@repo/o37-ui";
import { X, Send, Bot, Loader2 } from "lucide-react";
import { supabase } from '../supabaseClient';
import { MessageContent } from './AIAssistant/MessageContent';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  data?: unknown;
  suggestedActions?: string[];
  error?: boolean;
  action?: string;
}

interface AIAssistantSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  fundraiseId?: string;
}

export default function AIAssistantSidebar({ isOpen, onClose, fundraiseId }: AIAssistantSidebarProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hi! I\'m your fundraising assistant. I can help you manage your pipeline, track funds, log activities, and analyze your fundraise. What would you like to do?',
      timestamp: new Date(),
      suggestedActions: [
        'Show pipeline overview',
        'List all Tier 1 funds',
        'What\'s our committed capital?'
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get the current session token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('Failed to get auth session:', error);
      return null;
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      if (!fundraiseId) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Please select a fundraise first to use the AI assistant.',
          timestamp: new Date(),
          error: true
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      }

      // Get auth token
      const token = await getAuthToken();
      
      if (!token) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Your session has expired. Please log in again to continue.',
          timestamp: new Date(),
          error: true
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      }

      const response = await fetch(`${API_URL}/fundraising/raises/${fundraiseId}/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: userMessage.content
        })
      });

      if (response.status === 401) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'Your session has expired. Please log in again to continue.',
          timestamp: new Date(),
          error: true
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      }

      if (response.status === 403) {
        const data = await response.json();
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.message || 'You do not have permission to access this fundraise.',
          timestamp: new Date(),
          error: true
        };
        setMessages(prev => [...prev, assistantMessage]);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to get response');
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.error && !data.success 
          ? `${data.message}\n\nError details: ${data.error}`
          : data.message,
        timestamp: new Date(),
        data: data.data,
        suggestedActions: data.suggestedActions,
        action: data.action,
        error: !data.success && data.error ? true : false
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (e) {
      console.error('AI request error:', e);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: e instanceof Error ? e.message : 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        error: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedAction = (action: string) => {
    setInput(action);
  };

  if (!isOpen) return null;

  return (
    <div className="w-[360px] flex-shrink-0 flex flex-col h-full py-3 pr-3">
      <div className="flex-1 flex flex-col bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-neutral-600" />
            <span className="font-medium">AI Assistant</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[90%] ${message.role === 'user' ? 'order-2' : ''}`}>
                <div className={`rounded-2xl px-4 py-2.5 ${
                  message.role === 'user'
                    ? 'bg-black text-white'
                    : message.error 
                      ? 'bg-red-50 text-red-900 border border-red-100'
                      : 'bg-neutral-100 text-neutral-900'
                }`}>
                  {message.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  ) : (
                    <MessageContent 
                      content={message.content}
                      data={message.data}
                      action={message.action}
                      isError={message.error}
                    />
                  )}
                </div>

                {/* Suggested Actions */}
                {message.suggestedActions && message.suggestedActions.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {message.suggestedActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestedAction(action)}
                        className="text-xs px-2.5 py-1 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 rounded-full border border-neutral-200 transition-colors"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                )}

                <div className="text-[10px] text-neutral-400 mt-1 px-1">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-neutral-100 rounded-2xl px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-3 border-t border-neutral-100">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about your fundraise..."
              disabled={loading}
              className="flex-1"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-3"
              icon={<Send className="w-4 h-4" />}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
